import React from 'react'
import { DateFns, YearMonthFns } from '../utils/datetime'
import { OverrideComponentProps } from '../utils/overridable-component-helper'
import { DatePickerContext } from './date-picker-root'
import { composeEventHandlers } from '../utils/compose-event-handlers'
import { composeRefs } from '../utils/compose-refs'
import { DatePickerMonthContext } from './date-picker-month'

export type DatePickerDayProps<Tag extends React.ElementType<any>> = OverrideComponentProps<
  Tag,
  {
    day: number
    date: string
    isCurrentMonth: boolean
    isToday: boolean
    selected: boolean
    active: boolean
    disabled: boolean
  }
> & { date: string }

export const DatePickerDay = React.forwardRef<HTMLDivElement, DatePickerDayProps<any>>(
  ({ date, as = 'button', className, children, ...props }, forwardRef) => {
    const context = React.useContext(DatePickerContext)
    const monthContext = React.useContext(DatePickerMonthContext)
    if (context && monthContext) {
      const ref = React.useRef<any>(null)
      React.useEffect(() => {
        if (context.focus === date && ['none', 'init'].includes(context.transitionState)) {
          ref.current?.focus()
        }
      }, [context.focus, context.transitionState, date, ref])
      const onKeyDown = React.useCallback(
        (event: React.KeyboardEvent<any>) => {
          function moveFocus(days: number) {
            const newDate = DateFns.addDays(date, days)
            context?.setFocus(newDate)
          }
          if (event.key === 'ArrowUp') {
            moveFocus(-7)
          } else if (event.key === 'ArrowDown') {
            moveFocus(7)
          } else if (event.key === 'ArrowLeft') {
            moveFocus(-1)
          } else if (event.key === 'ArrowRight') {
            moveFocus(1)
          } else if (event.key === 'Home') {
            const dayOfWeek = new Date(`${date}T00:00Z`).getUTCDay()
            moveFocus(-dayOfWeek)
          } else if (event.key === 'End') {
            const dayOfWeek = new Date(`${date}T00:00Z`).getUTCDay()
            moveFocus(6 - dayOfWeek)
          } else if (event.key === 'PageUp') {
            moveFocus(YearMonthFns.getDaysInMonth(monthContext.yearMonth))
          } else if (event.key === 'PageDown') {
            moveFocus(-YearMonthFns.getDaysInMonth(YearMonthFns.getPrev(monthContext.yearMonth)))
          }
        },
        [date, monthContext.yearMonth, context]
      )

      const isDisabled = React.useMemo(() => context.shouldDisableDate(date), [date, context])
      const slots = {
        date,
        day: DateFns.getDate(date),
        isCurrentMonth: YearMonthFns.getMonthNumber(monthContext.yearMonth) === DateFns.getMonth(date),
        isToday: DateFns.toISODate(new Date()) === date,
        selected: date === context.value,
        active: date === context.focus,
        disabled: isDisabled
      }
      return React.createElement(
        as,
        {
          ...props,
          ref: composeRefs(forwardRef, ref),
          onKeyDown: composeEventHandlers(props.onKeyDown, onKeyDown),
          onClick: composeEventHandlers(props.onClick, () => !isDisabled && context.onChange(date)),
          className: typeof className === 'function' ? className(slots) : className
        },
        ...React.Children.toArray(typeof children === 'function' ? children(slots) : children)
      )
    } else {
      throw new Error('Cannot find date picker context')
    }
  }
) as WithForwardRefType

DatePickerDay.displayName = 'DatePickerDay'

interface WithForwardRefType extends React.FC<DatePickerDayProps<React.ElementType<any>>> {
  <T extends React.ElementType<any> = 'button'>(props: DatePickerDayProps<T>): ReturnType<React.FC<DatePickerDayProps<T>>>
}
